import React, { useCallback, useMemo, useState } from 'react';

import type { EventRecord } from '@vlabs/api/artemy/model';
import { getArtemySampleUrl, proxyArtemyUrl } from '@vlabs/api/axios/point2-client';
import {
  ZoomInIcon, DownloadIcon, SearchIcon,
} from '@vlabs/icons';
import { useImage } from '@vlabs/shared/hooks';
import { useOutsideClick } from '@vlabs/shared/hooks/useOutsideClick';
import { parseISOToLocale } from '@vlabs/shared/utils/date';
import {
  BodySample, Control, FaceSample, useModal, ButtonScrimGroup,
} from '@vlabs/uikit';
import { IconButton } from '@vlabs/uikit/components/controls/button/icon-button/IconButton';
import { routes } from '@vlapps/point/src/app/routes';
import cn from 'classnames';
import qs from 'qs';
import { useTranslation } from 'react-i18next';
import { generatePath, useHistory } from 'react-router-dom';

import { downloadImage } from '@vlabs/monokit/shared/lib/images';
import { FullFrame } from '@vlabs/monokit/shared/ui/full-frame';
import { FullFrameWithDetections } from '@vlabs/monokit/shared/ui/full-frame-with-detections';
import { StreamLinkPoint3 } from '@vlabs/monokit/shared/ui/stream-link/StreamLink.point3';

import st from './EventFrame.module.sass';
import { FullFrameWithInteractiveBBox } from './legacy/FullFrameWithInteractiveBBox.jsx';
import { AttachFaceToCardButtonPoint3 } from '../../../widgets/attach-face-to-card-button';

const initialSampleState = { type: null, state: false };

const isImageOrigin = (imageOrigin?: string | null): imageOrigin is string => (
  !!imageOrigin && /^\/(1|6)\/images\//u.test(imageOrigin)
);

const getImageUrl = (imageOrigin?: string | null): string | undefined => {
  if (!isImageOrigin(imageOrigin)) return undefined;

  return proxyArtemyUrl(imageOrigin.replace(/^\/6\/images\//u, '/1/images/'));
};

export function EventFrame({ event }: { event: EventRecord }): React.JSX.Element {
  const { t } = useTranslation();
  const history = useHistory();
  const [isSampleVisible, setIsSampleVisible] = useState(initialSampleState);

  const fullFrameModal = useModal({ className: [st.FullFrameModal] });

  const detectionWithFullFrame = event?.face_detections?.find(({ image_origin }) => isImageOrigin(image_origin))
    ?? event?.body_detections?.find(({ image_origin }) => isImageOrigin(image_origin));
  const fullFrameUrl = getImageUrl(detectionWithFullFrame?.image_origin);
  const fullFrame = useImage(fullFrameUrl);

  const bodyDetection = event?.body_detections?.find(({ sample_id }) => !!sample_id);
  const bodySample = useImage(getArtemySampleUrl('bodies', bodyDetection?.sample_id));

  const faceDetection = event?.face_detections?.find(({ sample_id }) => !!sample_id);
  const faceSample = useImage(getArtemySampleUrl('faces', faceDetection?.sample_id));

  const uiDetections = useMemo(
    () => [
      bodySample.url && bodyDetection && { ...bodyDetection.detection?.rect, type: 'body' },
      faceSample.url && faceDetection && { ...faceDetection.detection?.rect, type: 'face' },
    ].filter(Boolean),
    [bodyDetection, bodySample.url, faceDetection, faceSample.url],
  );

  const sampleDetailRef = useOutsideClick((e) => {
    if (e.target.dataset.overlayAction === 'close') setIsSampleVisible(initialSampleState);
  });

  const onSearch = useCallback(() => {
    history.push({
      pathname: generatePath(routes.search),
      search: qs.stringify({
        references: [{
          id: event.event_id,
          type: 'event',
          referenceType: 'event',
          candidateOrigin: 'events',
        }],
      }),
    });
  }, [event?.event_id, history]);

  const onDownloadSamples = useCallback((_, type) => {
    if (!type && fullFrame.url) downloadImage(fullFrame.url);
    if (type === 'face' && faceSample.url) downloadImage(faceSample.url);
    if (type === 'body' && bodySample.url) downloadImage(bodySample.url);
  }, [bodySample.url, faceSample.url, fullFrame.url]);

  const onSampleClick = (type) => {
    setIsSampleVisible({ type, state: true });
  };

  const renderActions = (type) => (
    <ButtonScrimGroup
      className={st.Actions}
      size="small"
    >
      <IconButton
        data-testid="openFullScreenModalButton"
        disabled={!fullFrame.url || fullFrame.error}
        icon={<ZoomInIcon />}
        kind="secondary"
        onClick={() => fullFrameModal.open()}
        size="small"
        variant="flat"
      />
      <IconButton
        data-testid="workspace.eventDetails.downloadButton"
        icon={<DownloadIcon />}
        kind="secondary"
        onClick={(e) => onDownloadSamples(e, type)}
        size="small"
        variant="flat"
      />
    </ButtonScrimGroup>
  );

  if (!event) return null;

  const hasNoValidImage = fullFrame.error && faceSample.error && bodySample.error;

  if (hasNoValidImage) {
    return (
      <div
        className={st.NoData}
        data-testid="workspace.eventDetails.emptyContent"
      >
        {t('lunapoint:панель оператора.no data')}
      </div>
    );
  }

  return (
    <div className={st.Root}>
      {fullFrameModal.wrap(
        <FullFrameWithDetections
          data-testid="workspace.eventDetails.fullScreenModal.image"
          detections={{
            type: 'bbox',
            coordinates: uiDetections,
            color: 'positive',
          }}
          src={fullFrame.url}
        />,
      )}
      <div className={st.Header}>
        <div className={st.InfoBlock}>
          <div
            className={st.TimeText}
            data-testid="workspace.eventDetails.createTime"
          >
            {event.create_time ? parseISOToLocale(event.create_time) : '-'}
          </div>
          <div className={st.DotDivider} />
          <div
            className={st.StreamText}
            data-testid="workspace.eventDetails.source"
          >
            <StreamLinkPoint3
              className={st.StreamLink}
              hasTruncatedLink
              hideId
              kind="primary"
              placeholders={{
                removed: t('lunapoint:карточка события.videostream.removed'),
                unavailable: t('lunapoint:карточка события.videostream.unavailable'),
              }}
              size="medium"
              streamId={event.stream_id}
            />
          </div>
        </div>

        <div className={st.EventActions}>
          {!!faceSample.url && !!faceDetection?.sample_id && (
            <AttachFaceToCardButtonPoint3
              sampleId={faceDetection.sample_id}
              size="small"
            />
          )}
          <Control.Button
            data-testid="workspace.eventDetails.search"
            kind="secondary"
            onClick={onSearch}
            size="small"
            variant="filled"
          >
            <SearchIcon />
            <div className={st.ButtonContent}>
              {t('lunapoint:кнопки.искать')}
            </div>
          </Control.Button>
        </div>
      </div>

      {!!fullFrame.url && (
        <div className={st.FullFrame}>
          <div className={st.ImageContainer}>
            <div
              className={st.BackgroundImage}
              style={{ backgroundImage: `url(${fullFrame.url})` }}
            />
            <FullFrame
              data-testid="workspace.eventDetails.staticFullFrame"
              src={fullFrame.url}
            />
          </div>

          <div
            className={cn(
              st.InteractiveImageContainer,
              { [st.InteractiveImageContainer_hide]: isSampleVisible.state },
            )}
          >
            <FullFrameWithInteractiveBBox
              detections={uiDetections}
              imageUrl={fullFrame.url}
              onClick={onSampleClick}
              testId="workspace.eventDetails.interactiveFullFrame"
            />

            {renderActions(isSampleVisible.type)}
          </div>

          {!!isSampleVisible.state && (
            <div
              className={st.SampleModal}
              data-overlay-action="close"
            >
              <div
                className={st.SampleWrapper}
                ref={sampleDetailRef}
              >
                {isSampleVisible.type === 'face' ? (
                  <FaceSample
                    buttonOnBottom={renderActions('face')}
                    className={cn(st.Sample, st.Sample_face)}
                    data-testid="workspace.eventDetails.modalSampleImage.face"
                    src={faceSample.url}
                  />
                ) : (
                  <BodySample
                    buttonOnBottom={renderActions('body')}
                    className={cn(st.Sample, st.Sample_body)}
                    data-testid="workspace.eventDetails.modalSampleImage.body"
                    src={bodySample.url}
                  />
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {!!fullFrame.error && (
        <div className={st.FullFrameEmpty}>
          {!!faceSample.url && (
            <FaceSample
              buttonOnBottom={renderActions('face')}
              className={cn(st.Sample, st.Sample_face)}
              data-testid="workspace.eventDetails.sampleImage.face"
              src={faceSample.url}
            />
          )}

          {!!bodySample.url && (
            <BodySample
              buttonOnBottom={renderActions('body')}
              className={cn(st.Sample, st.Sample_body)}
              data-testid="workspace.eventDetails.sampleImage.body"
              src={bodySample.url}
            />
          )}
        </div>
      )}
    </div>
  );
}
